import { supabase } from '@/lib/supabase';
import { removeMedia, uploadProjectMedia } from '@/lib/storage';
import type { RoomImageKind, RoomImageRow } from '@/types/database';

export async function listRoomImages(roomId: string): Promise<RoomImageRow[]> {
  const { data, error } = await supabase
    .from('room_images')
    .select('*')
    .eq('room_id', roomId)
    .order('kind', { ascending: true })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function addRoomImage(
  file: File,
  projectId: string,
  roomId: string,
  kind: RoomImageKind,
  caption: string | null = null,
): Promise<RoomImageRow> {
  const { path } = await uploadProjectMedia(file, projectId, `rooms/${roomId}`);
  const { data, error } = await supabase
    .from('room_images')
    .insert({ room_id: roomId, storage_path: path, kind, caption })
    .select()
    .single();
  if (error) {
    await removeMedia(path).catch(() => {});
    throw error;
  }
  return data;
}

export async function updateRoomImageCaption(
  id: string,
  caption: string | null,
): Promise<RoomImageRow> {
  const { data, error } = await supabase
    .from('room_images')
    .update({ caption })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteRoomImage(row: RoomImageRow): Promise<void> {
  // Mirror files.ts: delete the storage object first, then the DB row. If the
  // row delete fails the object is already gone — orphan-free is more useful
  // than transactional symmetry here.
  await removeMedia(row.storage_path).catch(() => {});
  const { error } = await supabase.from('room_images').delete().eq('id', row.id);
  if (error) throw error;
}
